<script setup>
import {usePortalContentAndDesignStore} from "../../../../stores/partners/portalContentAndDesignStore";
import {defineProps, computed, watch} from "vue";

const props = defineProps({
    authView: {
        type: Boolean,
        default: false
    }
});

const portalContentAndDesignStore =  usePortalContentAndDesignStore()

const isModernLayout = computed(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout === 'one');

// Layout style options - exclude 'None' for Modern layout
const layoutStyleOptions = computed(() => {
    const options = [
        { value: 'border', label: 'Border'},
        { value: 'shadow', label: 'Shadow'},
        { value: 'bald', label: 'Bald'},
    ];

    // Only add 'None' option for non-Modern layouts
    if (!isModernLayout.value) {
        options.push({ value: 'none', label: 'None'});
    }

    return options;
});

// When switching to Modern layout, reset layout style if it's 'none'
watch(() => portalContentAndDesignStore.portalLayoutSettings.portal_layout, (newLayout) => {
    if (newLayout === 'one' && portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style === 'none') {
        portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style = 'border';
    }
});
</script>

<template>
    <radio-group-inline
        label="Style"
        name="portal_layout"
        :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout"
        @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout = $event"
        :options="[
            { value: 'one', label: 'Modern'},
            { value: 'two', label: 'Casual'},
            { value: 'three', label: 'Sidebar'},
        ]"
    >
    </radio-group-inline>

    <radio-group-inline
        label="Card style"
        name="portal_layout_layout_style"
        :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style"
        @update:model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_style = $event"
        :options="layoutStyleOptions"
    >
    </radio-group-inline>

    <radio-group-inline-color
        label="Corners"
        name="portal_layout_layout_corners"
        :model-value="portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_corners"
        @picked="portalContentAndDesignStore.portalLayoutSettings.portal_layout_layout_corners = $event.finalValue"
        :options="[
            {
                value: 'none',
                label: 'data:image/svg+xml;base64,PHN2ZyBjbGFzcz0idy00IGgtNCIgd2lkdGg9IjI4cHgiIGhlaWdodD0iMjhweCIgdmlld0JveD0iMCAwIDI4IDI4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPiA8cmVjdCB4PSIyIiB5PSIyIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHN0cm9rZT0iY3VycmVudENvbG9yIiBzdHJva2Utd2lkdGg9IjQiIGZpbGw9Im5vbmUiPjwvcmVjdD48L3N2Zz4=',
                label_is_img: true
            },
            {
                value: 'rounded-lg',
                label: 'data:image/svg+xml;base64,PHN2ZyBjbGFzcz0idy00IGgtNCIgd2lkdGg9IjI4cHgiIGhlaWdodD0iMjhweCIgdmlld0JveD0iMCAwIDI4IDI4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPiA8cmVjdCB4PSIyIiB5PSIyIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHJ4PSI0IiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSI0IiBmaWxsPSJub25lIj48L3JlY3Q+PC9zdmc+',
                label_is_img: true
            },
            {
                value: 'rounded-xl',
                label: 'data:image/svg+xml;base64,PHN2ZyBjbGFzcz0idy00IGgtNCIgd2lkdGg9IjI4cHgiIGhlaWdodD0iMjhweCIgdmlld0JveD0iMCAwIDI4IDI4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPiA8cmVjdCB4PSIyIiB5PSIyIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHJ4PSI4IiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSI0IiBmaWxsPSJub25lIj48L3JlY3Q+PC9zdmc+',
                label_is_img: true
            },
            {
                value: 'rounded-3xl',
                label: 'data:image/svg+xml;base64,PHN2ZyBjbGFzcz0idy00IGgtNCIgd2lkdGg9IjI4cHgiIGhlaWdodD0iMjhweCIgdmlld0JveD0iMCAwIDI4IDI4IiB2ZXJzaW9uPSIxLjEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiPiA8cmVjdCB4PSIyIiB5PSIyIiB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHJ4PSIxMCIgc3Ryb2tlPSJjdXJyZW50Q29sb3IiIHN0cm9rZS13aWR0aD0iNCIgZmlsbD0ibm9uZSI+PC9yZWN0Pjwvc3ZnPg==',
                label_is_img: true
            }
        ]"
    >
    </radio-group-inline-color>
</template>
